/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
import '@total-typescript/ts-reset';

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom/client';
import { client } from '~/generated/client.gen';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isAxiosError } from 'axios';
import * as configs from '~/configs';
import UserDefinedConfigProvider from './containers/UserDefinedConfigProvider';
import UnknownLocaleModal from './containers/UILanguage/UnknownLocaleModal';
import { PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';
import AppErrorBoundary from './components/AppErrorBoundary';
import { RouterProvider } from '@tanstack/react-router';
// Import the CSS reset, which HtmlWebpackPlugin transfers to the build folder
import 'sanitize.css';
import './global.css';

// Import i18n messages
import { translationMessages } from './i18n';
import { UILanguageProvider } from './containers/UILanguage';
import ErrorPage from './components/ErrorPage';
import { SnackbarProvider } from './containers/Snackbar/SnackbarContext';
import { DrawerMenuProvider } from './containers/DrawerMenu/DrawerMenuContext';
import { router } from './router';

client.setConfig({
  baseURL: import.meta.env.PROD
    ? configs.production.api.slice(0, -1)
    : window.location.origin + configs.development.api.slice(0, -1),
});

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    // Mutation errors are handled locally (snackbars) and never bubble up to
    // capture_exceptions, so report them to PostHog here.
    onError: (error) => {
      try {
        // captureException only serializes name/message/stack. Axios keeps the
        // request (config) and the server's answer (response) in nested objects
        // it never walks — pull them out so a bare "status code 422" is
        // actionable.
        const axiosContext = isAxiosError(error)
          ? {
              request_method: error.config?.method,
              request_url: error.config?.url,
              request_base_url: error.config?.baseURL,
              request_body: error.config?.data,
              response_status: error.response?.status,
              response_status_text: error.response?.statusText,
              response_body: error.response?.data,
            }
          : undefined;
        posthog.captureException?.(error, axiosContext);
      } catch {
        // PostHog may not be initialized — silently ignore
      }
    },
  }),
});

const render = (translatedMessages: Record<string, Record<string, string>>) => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        {!import.meta.env.VITE_E2E && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
        <PostHogProvider client={posthog}>
          <SnackbarProvider>
            <DrawerMenuProvider>
              <UILanguageProvider>
                <UserDefinedConfigProvider messages={translatedMessages}>
                  <AppErrorBoundary
                    fallback={
                      <ErrorPage onRetry={() => window.location.reload()} />
                    }
                  >
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <RouterProvider router={router} />
                      <UnknownLocaleModal />
                    </LocalizationProvider>
                  </AppErrorBoundary>
                </UserDefinedConfigProvider>
              </UILanguageProvider>
            </DrawerMenuProvider>
          </SnackbarProvider>
        </PostHogProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
};

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    .then(() => Promise.all([import('intl/locale-data/jsonp/de')]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}
